import { ClubBookingApiClient } from "../../common/api/ClubBookingApiClient";
import { ClientConfig } from "../../model/client/ClientSettings";
import { ClientSettingAuditRow } from "../../model/client/ClientSettingAuditRow";
import { UpdateClientIdentityRequest } from "../../model/client/UpdateClientIdentityRequest";
import { UpdateClientThemeRequest } from "../../model/client/UpdateClientThemeRequest";
import { UpdateClientSettingsRequest } from "../../model/client/UpdateClientSettingsRequest";
import { UpdateClientBillingRequest } from "../../model/client/UpdateClientBillingRequest";
import { UpdateClientContactRequest } from "../../model/client/UpdateClientContactRequest";
import { UpdateClientMembershipCardRequest } from "../../model/client/UpdateClientMembershipCardRequest";
import { UpdateClientSignupRequest } from "../../model/client/UpdateClientSignupRequest";
import { UpdateClientCancellationRequest } from "../../model/client/UpdateClientCancellationRequest";
import { UpdateClientDirectDebitRequest } from "../../model/client/UpdateClientDirectDebitRequest";
import { UpdateClientBookingPricingRequest } from "../../model/client/UpdateClientBookingPricingRequest";
import { MembershipCategoryRow } from "../../model/members/MembershipCategoryRow";
import { FacilityScheduleEntry } from "../../model/facilities/FacilityScheduleEntry";
import { CreateMembershipCategoryRequest } from "../../requests/member/CreateMembershipCategoryRequest";
import { UpdateMembershipCategoryRequest } from "../../requests/member/UpdateMembershipCategoryRequest";
import { FacilityMetadataEntry, FacilityRow } from "../../model/facilities/FacilityRow";
import { CreateFacilityRequest } from "../../requests/facility/CreateFacilityRequest";
import { UpdateFacilityRequest } from "../../requests/facility/UpdateFacilityRequest";
import { ReplaceFacilityMetadataRequest } from "../../requests/facility/ReplaceFacilityMetadataRequest";
import { ReplaceFacilityScheduleRequest } from "../../requests/facilities/ReplaceFacilityScheduleRequest";
import { CreateFacilityTypeRequest } from "../../requests/facility/CreateFacilityTypeRequest";
import { UpdateFacilityTypeRequest } from "../../requests/facility/UpdateFacilityTypeRequest";
import { FacilityTypeRow } from "../../model/facilities/FacilityTypeRow";
import { CreatePosItemTypeRequest } from "../../requests/pointofsale/CreatePosItemTypeRequest";
import { UpdatePosItemTypeRequest } from "../../requests/pointofsale/UpdatePosItemTypeRequest";
import { PosItemTypeRow } from "../../model/pointofsale/PosItemTypeRow";
import { ClientMetadataRow } from "../../model/client/ClientMetadataRow";
import { ReplaceClientMetadataRequest } from "../../model/client/ReplaceClientMetadataRequest";
import {
    ReplaceShiftConfigPayload,
    ShiftConfigDefinition,
} from "../../model/members/ShiftChecklist";

export class ClientConfigService extends ClubBookingApiClient {
    // App-shell branding loader: resolves the active tenant via the X-Client-Id
    // header. The settings page uses the by-id variants below so it can render
    // a tenant that isn't the one currently selected in the app shell.
    async getCurrentConfig(): Promise<ClientConfig | undefined> {
        return this.get<ClientConfig>(
            "/clients/current/config",
            undefined,
            { showLoading: false }
        );
    }

    // Self-service "register with this club" for an already-authenticated member
    // who isn't yet assigned to the current tenant (the config-load error screen).
    // Adds the tenant to the user's Auth0 clients list; the caller must then force
    // a token refresh so the new `clients` claim lands and config can load. On a
    // guard/eligibility failure the base client surfaces the server's message and
    // throws, so callers can just await and let the notification show.
    async registerCurrentClient(): Promise<{ requiresTokenRefresh: boolean }> {
        const result = await this.post<{ requiresTokenRefresh: boolean }>(
            "/clients/current/register",
            {},
            { showLoading: false, showSuccess: false }
        );
        return { requiresTokenRefresh: result?.requiresTokenRefresh ?? false };
    }

    async getConfig(clientId: number): Promise<ClientConfig | undefined> {
        return this.get<ClientConfig>(
            `/clients/${clientId}/config`,
            undefined,
            { showLoading: false }
        );
    }

    // FORT-153: generic settings audit reader. Each section UI passes its own
    // keyPrefix (theme uses "branding.", locale would use "locale.", …) and
    // groups the returned rows client-side into snapshots that fit its own
    // completeness rule.
    async getSettingsHistory(
        clientId: number,
        params: { keyPrefix: string; limit?: number }
    ): Promise<ClientSettingAuditRow[]> {
        const query = new URLSearchParams({ keyPrefix: params.keyPrefix });
        if (params.limit !== undefined) query.set('limit', String(params.limit));
        const rows = await this.get<ClientSettingAuditRow[]>(
            `/clients/${clientId}/settings/history?${query.toString()}`,
            undefined,
            { showLoading: false }
        );
        return rows ?? [];
    }

    async updateIdentity(clientId: number, payload: UpdateClientIdentityRequest): Promise<void> {
        await this.put<void>(
            `/clients/${clientId}/settings/identity`,
            payload,
            { successMessage: "Identity settings saved." }
        );
    }

    async updateTheme(clientId: number, payload: UpdateClientThemeRequest): Promise<void> {
        await this.put<void>(
            `/clients/${clientId}/settings/theme`,
            payload,
            { successMessage: "Theme settings saved." }
        );
    }

    // Club-admin editable (FORT-288), so it targets the current tenant (X-Client-Id)
    // rather than a /clients/{clientId} route. Throws on a 400 so the caller keeps
    // the unsaved form.
    async updateBookingPricing(payload: UpdateClientBookingPricingRequest): Promise<void> {
        await this.put<void>(
            "/clients/current/settings/booking-pricing",
            payload,
            { successMessage: "Booking prices saved." }
        );
    }

    // Fortius admin settings shell variant of updateBookingPricing: any club, by id.
    async updateBookingPricingForClient(clientId: number, payload: UpdateClientBookingPricingRequest): Promise<void> {
        await this.put<void>(
            `/clients/${clientId}/settings/booking-pricing`,
            payload,
            { successMessage: "Booking prices saved." }
        );
    }

    async updateBilling(clientId: number, payload: UpdateClientBillingRequest): Promise<void> {
        await this.put<void>(
            `/clients/${clientId}/settings/billing`,
            payload,
            { successMessage: "Billing settings saved." }
        );
    }

    async updateContact(clientId: number, payload: UpdateClientContactRequest): Promise<void> {
        await this.put<void>(
            `/clients/${clientId}/settings/contact`,
            payload,
            { successMessage: "Contact settings saved." }
        );
    }

    async updateMembershipCard(
        clientId: number,
        payload: UpdateClientMembershipCardRequest
    ): Promise<void> {
        await this.put<void>(
            `/clients/${clientId}/settings/membership-card`,
            payload,
            { successMessage: "Membership card layout saved." }
        );
    }

    async updateSignup(clientId: number, payload: UpdateClientSignupRequest): Promise<void> {
        await this.put<void>(
            `/clients/${clientId}/settings/signup`,
            payload,
            { successMessage: "Signup settings saved." }
        );
    }

    async updateCancellation(clientId: number, payload: UpdateClientCancellationRequest): Promise<void> {
        await this.put<void>(
            `/clients/${clientId}/settings/cancellation`,
            payload,
            { successMessage: "Cancellation settings saved." }
        );
    }

    async updateDirectDebit(clientId: number, payload: UpdateClientDirectDebitRequest): Promise<void> {
        await this.put<void>(
            `/clients/${clientId}/settings/direct-debit`,
            payload,
            { successMessage: "Direct debit settings saved." }
        );
    }

    // Resolves the active tenant's signup pricing-sheet (from the X-Client-Id
    // header) to a short-lived presigned URL the guest form renders as an image.
    // Returns null when none is configured (endpoint 404s) so callers hide the
    // section. Errors are swallowed (showError:false) — a missing sheet must not
    // surface as a failure on the signup form.
    async getSignupPricingSheetUrl(): Promise<string | null> {
        try {
            const resp = await this.get<{ url: string }>(
                "/clients/current/signup/pricing-sheet-url",
                undefined,
                { showLoading: false, showError: false }
            );
            return resp?.url ?? null;
        } catch {
            return null;
        }
    }

    // Resolves the active tenant's membership-card header logo (membershipCard
    // header.logoKey) to a base64 data URL the card generator embeds in the PDF.
    // Served as base64 (not a presigned URL) so the generator's in-browser fetch
    // isn't blocked by S3 CORS. Returns '' when none is configured / the object is
    // missing, so the generator falls back to the text header. Errors are swallowed
    // (showError:false) — a missing logo must not surface as a failure.
    async getMembershipCardLogoData(): Promise<string> {
        try {
            const data = await this.get<string>(
                "/clients/current/membership-card/logo-data",
                undefined,
                { showLoading: false, showError: false }
            );
            return data ?? "";
        } catch {
            return "";
        }
    }

    // Generic settings upsert for the opaque-JSON config keys (fees.joining,
    // forms.*, banners.rules, checkin.birthdayMessage). Used by the joining-fee,
    // forms, banners and check-in section panels, and by the copy-from-another-
    // client flow which replays a source tenant's config onto the target.
    async updateSettings(clientId: number, payload: UpdateClientSettingsRequest): Promise<void> {
        await this.put<void>(
            `/clients/${clientId}/settings`,
            payload,
            { successMessage: "Settings saved." }
        );
    }

    async listMemberCategories(clientId: number): Promise<MembershipCategoryRow[]> {
        const rows = await this.get<MembershipCategoryRow[]>(
            `/clients/${clientId}/settings/member-categories`,
            undefined,
            { showLoading: false }
        );
        return rows ?? [];
    }

    async createMemberCategory(
        clientId: number,
        payload: CreateMembershipCategoryRequest
    ): Promise<MembershipCategoryRow> {
        return this.post<MembershipCategoryRow>(
            `/clients/${clientId}/settings/member-categories`,
            payload,
            { successMessage: "Member category created." }
        );
    }

    async updateMemberCategory(
        clientId: number,
        id: number,
        payload: UpdateMembershipCategoryRequest
    ): Promise<void> {
        await this.put<void>(
            `/clients/${clientId}/settings/member-categories/${id}`,
            payload,
            { successMessage: "Member category saved." }
        );
    }

    async deleteMemberCategory(clientId: number, id: number): Promise<void> {
        await this.delete<void>(
            `/clients/${clientId}/settings/member-categories/${id}`,
            { successMessage: "Member category deleted." }
        );
    }

    // FORT-130: facility CRUD + metadata bulk-replace.
    async listFacilities(clientId: number): Promise<FacilityRow[]> {
        const rows = await this.get<FacilityRow[]>(
            `/clients/${clientId}/settings/facilities`,
          undefined,
            { showLoading: false }
        );
        return rows ?? [];
    }
  
    // Facility schedule: lightweight dropdown source + read/replace of the
    // open/close windows for a single facility. Tenancy is resolved
    // server-side from X-Client-Id; the facilityId in the URL is the
    // per-tenant facility primary key.
    async listFacilitiesForScheduleEditor(): Promise<{ id: number; name: string; hasSchedule: boolean }[]> {
        const rows = await this.get<{ id: number; name: string; hasSchedule: boolean }[]>(
            `/facilities/schedule-editor/facilities`,
          undefined,
            { showLoading: false }
        );
        return rows ?? [];
    }

  
    async listFacilityTypes(clientId: number): Promise<FacilityTypeRow[]> {
        const rows = await this.get<FacilityTypeRow[]>(
            `/clients/${clientId}/settings/facility-types`,
            undefined,
            { showLoading: false }
        );
        return rows ?? [];
    }

    async createFacility(clientId: number, payload: CreateFacilityRequest): Promise<FacilityRow> {
        return this.post<FacilityRow>(
            `/clients/${clientId}/settings/facilities`,
            payload,
            { successMessage: "Facility created." }
        );
    }

    async updateFacility(clientId: number, id: number, payload: UpdateFacilityRequest): Promise<void> {
        await this.put<void>(
            `/clients/${clientId}/settings/facilities/${id}`,
            payload,
            { successMessage: "Facility saved." }
        );
    }

    async deleteFacility(clientId: number, id: number): Promise<void> {
        await this.delete<void>(
            `/clients/${clientId}/settings/facilities/${id}`,
            { successMessage: "Facility deleted." }
        );
    }

    async listFacilityMetadata(clientId: number, facilityId: number): Promise<FacilityMetadataEntry[]> {
        const rows = await this.get<FacilityMetadataEntry[]>(
            `/clients/${clientId}/settings/facilities/${facilityId}/metadata`,
          undefined,
            { showLoading: false }
        );
        return rows ?? [];
    }
  
    async getFacilitySchedule(facilityId: number): Promise<FacilityScheduleEntry[]> {
        const rows = await this.get<FacilityScheduleEntry[]>(
            `/facilities/${facilityId}/schedule`,
            undefined,
            { showLoading: false }
        );
        return rows ?? [];
    }

    async replaceFacilityMetadata(
        clientId: number,
        facilityId: number,
        payload: ReplaceFacilityMetadataRequest
    ): Promise<void> {
        await this.put<void>(
            `/clients/${clientId}/settings/facilities/${facilityId}/metadata`,
            payload,
            { successMessage: "Metadata saved." }
          );
    }
  
    async replaceFacilitySchedule(
        facilityId: number,
        payload: ReplaceFacilityScheduleRequest
    ): Promise<FacilityScheduleEntry[]> {
        const rows = await this.put<FacilityScheduleEntry[]>(
            `/facilities/${facilityId}/schedule`,
            payload,
            { successMessage: "Schedule saved." }
        );
        return rows ?? [];
    }

    // FORT-325: shift-checklist template config (CRM > Shift Checklists).
    // showError:true so a failed load throws instead of resolving to undefined — the editor
    // must not present an empty template as "no shifts configured", because saving that
    // would replace the client's real checklists with nothing.
    async getShiftConfig(): Promise<ShiftConfigDefinition[]> {
        const rows = await this.get<ShiftConfigDefinition[]>(
            "/dashboard/shifts/config",
            undefined,
            { showLoading: false, showError: true }
        );
        return rows ?? [];
    }

    async replaceShiftConfig(payload: ReplaceShiftConfigPayload): Promise<ShiftConfigDefinition[]> {
        const rows = await this.put<ShiftConfigDefinition[]>(
            "/dashboard/shifts/config",
            payload,
            { successMessage: "Shift checklists saved." }
        );
        return rows ?? [];
    }

    async createFacilityType(
        clientId: number,
        payload: CreateFacilityTypeRequest
    ): Promise<FacilityTypeRow> {
        return this.post<FacilityTypeRow>(
            `/clients/${clientId}/settings/facility-types`,
            payload,
            { successMessage: "Facility type created." }
        );
    }

    async updateFacilityType(
        clientId: number,
        id: number,
        payload: UpdateFacilityTypeRequest
    ): Promise<void> {
        await this.put<void>(
            `/clients/${clientId}/settings/facility-types/${id}`,
            payload,
            { successMessage: "Facility type saved." }
        );
    }

    async deleteFacilityType(clientId: number, id: number): Promise<void> {
        await this.delete<void>(
            `/clients/${clientId}/settings/facility-types/${id}`,
            { successMessage: "Facility type deleted." }
        );
    }

    // FORT-229: per-client POS item-type CRUD. Mirrors the facility-type
    // methods above — the settings section owns the list of rows and persists
    // each one directly.
    async listPosItemTypes(clientId: number): Promise<PosItemTypeRow[]> {
        const rows = await this.get<PosItemTypeRow[]>(
            `/clients/${clientId}/settings/pos-item-types`,
            undefined,
            { showLoading: false }
        );
        return rows ?? [];
    }

    async createPosItemType(
        clientId: number,
        payload: CreatePosItemTypeRequest
    ): Promise<PosItemTypeRow> {
        return this.post<PosItemTypeRow>(
            `/clients/${clientId}/settings/pos-item-types`,
            payload,
            { successMessage: "POS item type created." }
        );
    }

    async updatePosItemType(
        clientId: number,
        id: number,
        payload: UpdatePosItemTypeRequest
    ): Promise<void> {
        await this.put<void>(
            `/clients/${clientId}/settings/pos-item-types/${id}`,
            payload,
            { successMessage: "POS item type saved." }
        );
    }

    async deletePosItemType(clientId: number, id: number): Promise<void> {
        await this.delete<void>(
            `/clients/${clientId}/settings/pos-item-types/${id}`,
            { successMessage: "POS item type deleted." }
        );
    }

    // FORT-134: arbitrary tenant-scoped key/value metadata (audit-tracked
    // server-side). PUT is an authoritative bulk replace; DELETE removes one
    // key by name.
    async listClientMetadata(clientId: number): Promise<ClientMetadataRow[]> {
        const rows = await this.get<ClientMetadataRow[]>(
            `/clients/${clientId}/settings/metadata`,
            undefined,
            { showLoading: false }
        );
        return rows ?? [];
    }

    async replaceClientMetadata(
        clientId: number,
        payload: ReplaceClientMetadataRequest
    ): Promise<void> {
        await this.put<void>(
            `/clients/${clientId}/settings/metadata`,
            payload,
            { successMessage: "Metadata saved." }
        );
    }

    async deleteClientMetadata(clientId: number, name: string): Promise<void> {
        await this.delete<void>(
            `/clients/${clientId}/settings/metadata/${encodeURIComponent(name)}`,
            { successMessage: "Metadata entry deleted." }
        );
    }
}
